import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { Granularity, MonitoringFilters } from "@models/adminMonitoring";

dayjs.extend(utc);

export type WindowPreset = "24h" | "7d" | "30d" | "90d" | "custom";
export const WINDOW_PRESETS: WindowPreset[] = ["24h", "7d", "30d", "90d", "custom"];

/** URL keys of the dimension filters, in display order. */
export const FILTER_KEYS = ["org", "module", "service", "provider", "model", "capability", "currency", "user"] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

const PRESET_DAYS: Record<Exclude<WindowPreset, "24h" | "custom">, number> = { "7d": 7, "30d": 30, "90d": 90 };

/**
 * The `[from, to)` window of a preset or a custom calendar range, in UTC.
 *
 * Day presets and custom ranges are whole UTC days (so core answers them from its
 * daily rollups); a custom end date is inclusive on the calendar and becomes the
 * next midnight here, exactly once. 24h is hour-aligned.
 */
export const resolveWindow = (
  preset: WindowPreset,
  custom: { from?: string | null; to?: string | null },
  now: dayjs.Dayjs = dayjs.utc(),
): { from: string; to: string } => {
  if (preset === "24h") {
    const end = now.utc().startOf("hour").add(1, "hour");
    return { from: end.subtract(24, "hour").toISOString(), to: end.toISOString() };
  }
  if (preset === "custom" && custom.from && custom.to) {
    const start = dayjs.utc(custom.from).startOf("day");
    const end = dayjs.utc(custom.to).startOf("day").add(1, "day");
    if (start.isBefore(end)) return { from: start.toISOString(), to: end.toISOString() };
  }
  const days = preset === "custom" ? 7 : PRESET_DAYS[preset];
  const end = now.utc().startOf("day").add(1, "day");
  return { from: end.subtract(days, "day").toISOString(), to: end.toISOString() };
};

/** A readable number of buckets for the window. */
export const granularityFor = (from: string, to: string): Granularity => {
  const days = dayjs(to).diff(dayjs(from), "day", true);
  if (days <= 2) return "hour";
  if (days <= 45) return "day";
  return "week";
};

/**
 * Monitoring filters kept in the URL, so a reload or a shared link shows the
 * same view. Anything not in the URL is unfiltered.
 */
export const useMonitoringFilters = () => {
  const [params, setParams] = useSearchParams();

  const preset = (WINDOW_PRESETS as string[]).includes(params.get("range") ?? "")
    ? (params.get("range") as WindowPreset)
    : "7d";
  const customFrom = params.get("from");
  const customTo = params.get("to");

  const values = useMemo(() => {
    const out: Partial<Record<FilterKey, string>> = {};
    for (const key of FILTER_KEYS) {
      const value = params.get(key);
      if (value) out[key] = value;
    }
    return out;
  }, [params]);

  // Bumped by refresh(): re-resolves "now". Otherwise "now" is resolved only when
  // the selection changes, so the request key stays stable across renders.
  const tick = params.get("t");
  const window = useMemo(
    () => {
      void tick;
      return resolveWindow(preset, { from: customFrom, to: customTo });
    },
    [preset, customFrom, customTo, tick],
  );

  const filters: MonitoringFilters = useMemo(
    () => ({
      ...window,
      organisationId: values.org,
      module: values.module,
      service: values.service,
      provider: values.provider,
      model: values.model,
      capability: values.capability,
      currency: values.currency,
      userId: values.user,
    }),
    [window, values],
  );

  const update = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setFilter = useCallback((key: FilterKey, value: string | null) => update({ [key]: value }), [update]);

  const setPreset = useCallback(
    (next: WindowPreset, range?: { from: string; to: string }) =>
      update({ range: next, from: range?.from ?? null, to: range?.to ?? null }),
    [update],
  );

  const clearFilters = useCallback(
    () => update(Object.fromEntries(FILTER_KEYS.map((key) => [key, null]))),
    [update],
  );

  /** Re-resolve a relative window to "now" (the refresh button). */
  const refresh = useCallback(() => update({ t: String(Date.now()) }), [update]);

  return {
    filters,
    preset,
    /** Any other page-specific URL parameter (e.g. a chart split). */
    raw: (name: string) => params.get(name),
    update,
    custom: { from: customFrom, to: customTo },
    values,
    activeCount: Object.keys(values).length,
    setFilter,
    setPreset,
    clearFilters,
    refresh,
    /** A stable string of the effective filters, for request keys and paging resets. */
    key: JSON.stringify(filters),
    granularity: granularityFor(window.from, window.to),
  };
};
